import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext, API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { CheckCircle2, Circle, X, Rocket, KeyRound, Building2, Users, CreditCard, ClipboardCheck, Briefcase } from "lucide-react";

const ITEMS = {
  admin: [
    { id: "password", label: "Change your password", desc: "Replace the seeded password with your own.", icon: KeyRound, action: "password" },
    { id: "subscriptions", label: "Review subscriptions & billing", desc: "Check your plans and subscribers.", icon: CreditCard, path: "/admin/subscriptions" },
    { id: "trials", label: "Review demo & trial requests", desc: "Approve any incoming free-trial signups.", icon: ClipboardCheck, path: "/admin/demo" },
  ],
  company_owner: [
    { id: "password", label: "Change your password", desc: "Replace the seeded password with your own.", icon: KeyRound, action: "password" },
    { id: "company", label: "Set company details", desc: "Add your logo, ABN and contact info.", icon: Briefcase, path: "/company-profile" },
    { id: "sites", label: "Add your first site", desc: "Create the projects you're running.", icon: Building2, path: "/sites" },
    { id: "team", label: "Build your team", desc: "Add staff and assign them to sites.", icon: Users, path: "/site-managers" },
  ],
};

export const FirstRunChecklist = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const role = user?.role;
  const items = ITEMS[role];
  const storeKey = user ? `firstRunChecklist_${user.id}` : null;

  const [state, setState] = useState({ dismissed: false, done: {} });
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (!storeKey) return;
    try {
      const raw = localStorage.getItem(storeKey);
      if (raw) setState(JSON.parse(raw));
    } catch (e) { /* ignore */ }
  }, [storeKey]);

  const persist = (next) => {
    setState(next);
    if (storeKey) localStorage.setItem(storeKey, JSON.stringify(next));
  };

  // Permanently dismissed server-side (survives logout / new devices)
  if (!items || state.dismissed || user?.onboarding_dismissed) return null;

  const markDone = (id) => persist({ ...state, done: { ...state.done, [id]: true } });
  const dismiss = async () => {
    persist({ ...state, dismissed: true });
    try {
      await axios.post(`${API}/auth/dismiss-onboarding`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (refreshUser) refreshUser();
    } catch (e) { /* local dismiss still applies */ }
  };

  const handleItem = (item) => {
    if (item.action === "password") { setPwOpen(true); return; }
    markDone(item.id);
    if (item.path) navigate(item.path);
  };

  const doneCount = items.filter((i) => state.done[i.id]).length;
  const allDone = doneCount === items.length;

  return (
    <>
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm mb-6" data-testid="first-run-checklist">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/10 text-orange-600">
                <Rocket className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">
                  {allDone ? "You're all set \u{1F389}" : "Finish setting up your workspace"}
                </h3>
                <p className="text-sm text-slate-500">
                  {allDone ? "Nice work — you can hide this." : `A quick checklist to get going \u00B7 ${doneCount} of ${items.length} done`}
                </p>
              </div>
            </div>
            <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="first-run-dismiss" aria-label="Dismiss checklist">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {items.map((item) => {
              const Icon = item.icon;
              const done = !!state.done[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => handleItem(item)}
                  data-testid={`first-run-item-${item.id}`}
                  className={`text-left rounded-xl border p-3 transition-all ${done ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white hover:border-orange-300 hover:shadow-sm"}`}
                >
                  <div className="flex items-center gap-2">
                    {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                    <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </div>
                  <p className="mt-1 pl-6 text-xs text-slate-400">{item.desc}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} onSuccess={() => markDone("password")} />
    </>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange, onSuccess }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      toast.success("Password changed successfully");
      onSuccess && onSuccess();
      onOpenChange(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change your password</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current-input" />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" data-testid="cp-new-input" />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cp-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !current || !next} className="btn-construction" data-testid="cp-save-btn">
            {saving ? "Saving..." : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
